import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchAllData } from "../../helpers/externapi";
import {
    Button,
    Typography,
    CircularProgress,
    Box,
    TextField,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Avatar,
    Chip,
    Card,
    CardContent,
    Divider,
    Stack,
    alpha
} from "@mui/material";
import Layout from "../../Layout/Layout";
import PageWrapper from "../../Components/PageWrapper";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import CakeIcon from '@mui/icons-material/Cake';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { formatDate1 } from "../../Commoncomponents/CommonComponents";

export default function CommunityGroupCustomers() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [count, setCount] = useState(0);
    const [searchText, setSearchText] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const countResponse = await fetchAllData(
                    `lambdaAPI/CommunityCustomers/GetCountOfMembersByGroupId/${groupId}`
                );
                setCount(countResponse.data || 0);

                const customersResponse = await fetchAllData(
                    `lambdaAPI/CommunityCustomers/GetCommunityCustomersByGroup/${groupId}`
                );
                setCustomers(customersResponse);
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [groupId]);

    const filteredCustomers = customers.filter(customer =>
        Object.values(customer).some(
            value =>
                value &&
                value.toString().toLowerCase().includes(searchText.toLowerCase())
        )
    );

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getGenderColor = (gender) => {
        switch (gender?.toLowerCase()) {
            case 'male':
                return '#1976d2';
            case 'female':
                return '#e91e63';
            default:
                return '#757575';
        }
    };

    const getAvatarInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
    };

    return (
        <Layout>
            <PageWrapper>
                <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
                    {/* Header Section */}
                    <Card sx={{ 
                        mb: 3, 
                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                        borderRadius: 2,
                        border: 'none'
                    }}>
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <IconButton 
                                    onClick={() => navigate(-1)} 
                                    sx={{ 
                                        mr: 2,
                                        backgroundColor: alpha('#1976d2', 0.1),
                                        '&:hover': {
                                            backgroundColor: alpha('#1976d2', 0.2),
                                        }
                                    }}
                                >
                                    <ArrowBackIcon sx={{ color: '#1976d2' }} />
                                </IconButton>
                                <Box>
                                    <Typography variant="h4" component="h1" sx={{ 
                                        fontWeight: 700,
                                        color: '#1a202c',
                                        mb: 0.5
                                    }}>
                                        Group Members
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        Manage and view all customers in this community group
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search by name, mobile, or any field..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    sx={{ 
                                        minWidth: 320,
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            borderRadius: 2,
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#1976d2',
                                            }
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: <SearchIcon sx={{ color: '#64748b', mr: 1 }} />
                                    }}
                                />
                                
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Chip 
                                        label={`${count} Total Members`}
                                        color="primary"
                                        variant="filled"
                                        sx={{ fontWeight: 600 }}
                                    />
                                    <Chip 
                                        label={`${filteredCustomers.length} Showing`}
                                        color="default"
                                        variant="outlined"
                                        sx={{ fontWeight: 500 }}
                                    />
                                </Stack>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Table Section */}
                    {loading ? (
                        <Card sx={{ 
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            p: 6
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                                <CircularProgress size={48} thickness={4} sx={{ mb: 2 }} />
                                <Typography variant="body1" color="text.secondary">
                                    Loading customer data...
                                </Typography>
                            </Box>
                        </Card>
                    ) : (
                        <Card sx={{
                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: 'none'
                        }}>
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow sx={{
                                            backgroundColor: '#f1f5f9',
                                            '& .MuiTableCell-head': {
                                                color: '#475569',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                                py: 2,
                                                px: 3,
                                                borderBottom: '2px solid #e2e8f0',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em'
                                            }
                                        }}>
                                            <TableCell>Customer</TableCell>
                                            <TableCell align="center">Gender</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell align="center">Date of Birth</TableCell>
                                            <TableCell align="center">Age</TableCell>
                                            <TableCell align="center">Joined Date</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {filteredCustomers
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((customer, index) => (
                                                <TableRow
                                                    key={customer.CommunityCustomersId}
                                                    sx={{
                                                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafbfc',
                                                        '&:hover': {
                                                            backgroundColor: alpha('#1976d2', 0.04),
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                        },
                                                        transition: 'all 0.2s ease-in-out',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #f1f5f9'
                                                    }}
                                                >
                                                    <TableCell sx={{ px: 3, py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: getGenderColor(customer.Gender),
                                                                    width: 44,
                                                                    height: 44,
                                                                    fontWeight: 600,
                                                                    fontSize: '0.875rem'
                                                                }}
                                                            >
                                                                {getAvatarInitials(customer.Name)}
                                                            </Avatar>
                                                            <Box>
                                                                <Button
                                                                    onClick={() => {
                                                                        if (customer.CustomerId) {
                                                                            navigate(`/customers/details/${customer.CustomerId}`);
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        textTransform: "none",
                                                                        color: "#1976d2",
                                                                        fontWeight: 600,
                                                                        fontSize: '0.95rem',
                                                                        p: 0,
                                                                        minWidth: "auto",
                                                                        textAlign: 'left',
                                                                        '&:hover': {
                                                                            backgroundColor: 'transparent',
                                                                            color: '#1565c0',
                                                                            textDecoration: 'underline'
                                                                        }
                                                                    }}
                                                                    disabled={!customer.CustomerId}
                                                                >
                                                                    {customer.Name || 'N/A'}
                                                                </Button>
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    ID: {customer.CustomerId || 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell align="center" sx={{ px: 3, py: 2 }}>
                                                        <Chip
                                                            size="small"
                                                            label={customer.Gender || 'N/A'}
                                                            sx={{
                                                                backgroundColor: alpha(getGenderColor(customer.Gender), 0.1),
                                                                color: getGenderColor(customer.Gender),
                                                                fontWeight: 600,
                                                                minWidth: 70
                                                            }}
                                                        />
                                                    </TableCell>

                                                    <TableCell sx={{ px: 3, py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <PhoneIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {customer.MobileNumber || 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell align="center" sx={{ px: 3, py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <CakeIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                            <Typography variant="body2">
                                                                {customer.DateOfBirth ? formatDate1(customer.DateOfBirth) : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>

                                                    <TableCell align="center" sx={{ px: 3, py: 2 }}>
                                                        <Chip
                                                            size="small"
                                                            label={customer.Age ? `${customer.Age} years` : 'N/A'}
                                                            variant="outlined"
                                                            color="primary"
                                                            sx={{ minWidth: 80 }}
                                                        />
                                                    </TableCell>

                                                    <TableCell align="center" sx={{ px: 3, py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                            <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
                                                            <Typography variant="body2">
                                                                {customer.RegisteredOn ? formatDate1(customer.RegisteredOn) : 'N/A'}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                        {filteredCustomers.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                                        <PersonIcon sx={{ fontSize: 48, color: '#cbd5e1' }} />
                                                        <Typography variant="h6" color="text.secondary">
                                                            No customers found
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {searchText ? 'Try adjusting your search criteria' : 'This group has no members yet'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {filteredCustomers.length > 0 && (
                                <>
                                    <Divider />
                                    <TablePagination
                                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                                        component="div"
                                        count={filteredCustomers.length}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={handleChangeRowsPerPage}
                                        sx={{
                                            backgroundColor: '#f8fafc',
                                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                fontSize: '0.875rem',
                                                fontWeight: 500
                                            },
                                            '& .MuiTablePagination-select': {
                                                fontWeight: 600
                                            }
                                        }}
                                    />
                                </>
                            )}
                        </Card>
                    )}
                </Box>
            </PageWrapper>
        </Layout>
    );
}